import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CollapseCard } from 'components/UI';
import { useSelector } from 'react-redux';
import { ChangeUserStatusModal, AddCreditsModal } from 'components/Modals';
import i18n from 'i18n-js';

import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { makeStyles } from '@material-ui/core/styles';

// CollapseCard (components/UI) is still the MUI v4 implementation and only accepts
// classNames via `customClasses`, not sx objects — matches the same interop pattern
// used by other already-migrated widgets (e.g. AccessDevice/commonStyles.jsx).
const useCollapseCardStyles = makeStyles((theme) => ({
  root: {
    margin: '23px -25px 15px',
  },
  box: {
    borderWidth: '1px 0 0 0',
    borderRadius: '0 0 4px 4px',
    marginTop: 0,
    padding: 0,
  },
  buttonClass: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    padding: '14px 24px',
    color: theme.palette.primary.main,
    textTransform: 'capitalize',
    fontSize: '13px',
    borderTop: '1px solid #EEE',
    borderBottom: '1px solid #EEE',
    '&:hover': {
      backgroundColor: '#fff',
      boxShadow: 'none',
    },
  },
  buttonClassExtended: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    border: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      border: 0,
    },
  },
}));

const listItemSx = {
  paddingLeft: '24px',
  paddingRight: '24px',
};

const iconButtonSx = {
  marginRight: '-5px',
  '& svg': {
    fontSize: 14,
    fill: '#777777',
  },
};

const Actions = ({ id, listStyles }) => {
  const collapseCardClasses = useCollapseCardStyles();
  const { user } = useSelector((state) => state.users);
  const { loading } = useSelector((state) => state.users || {});
  const [openCreditModal, setOpenCreditModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const { account } = useSelector((state) => state.customers || {});
  const hasPricingCredits = useSelector(
    (state) => state.config?.settings?.core?.pricing?.credits || false
  );

  const [disableAction, setDisableAction] = useState({});

  useEffect(() => {
    if (!loading) {
      setDisableAction({});
    }
  }, [loading, setDisableAction]);

  const handleModalOpen = (type) => {
    if (type === 'status') {
      setOpenStatusModal((prevState) => !prevState);
    } else {
      setOpenCreditModal((prevState) => !prevState);
    }
  };

  const actions = [
    {
      title: i18n.t('users.change_status'),
      onClick: () => handleModalOpen('status'),
    },
    ...(hasPricingCredits
      ? [
          {
            title: i18n.t('customers.add_credits'),
            onClick: () => handleModalOpen('credit'),
          },
        ]
      : []),
  ];

  return (
    <CollapseCard title="Actions" customClasses={collapseCardClasses}>
      <List>
        {actions.map((item, index) => (
          <Fragment key={index}>
            <ListItem sx={listItemSx}>
              <ListItemText
                classes={listStyles}
                primary={i18n.t(`actions.${item.title}`, { defaultValue: item.title })}
              />
              <ListItemSecondaryAction>
                <IconButton
                  disabled={disableAction[item.title]}
                  onClick={item.onClick}
                  edge="end"
                  sx={iconButtonSx}
                  aria-label="right"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>

            {index !== actions.length - 1 && <Divider light={true} />}
          </Fragment>
        ))}
      </List>
      {openStatusModal && (
        <ChangeUserStatusModal
          id={id}
          open={openStatusModal}
          status={user?.status}
          handleClick={() => handleModalOpen('status')}
          userType="CUSTOMER"
        />
      )}
      {openCreditModal && (
        <AddCreditsModal
          to="driver"
          type="credit"
          account={account}
          user={user}
          accountLevel={true}
          open={openCreditModal}
          handleClick={() => handleModalOpen('credit')}
        />
      )}
    </CollapseCard>
  );
};

Actions.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  listStyles: PropTypes.object,
};

export default Actions;
